package com.oasisfeng.invaluable.event

/**
 * How much of a node's export a durable copy already holds.
 *
 * The three counts are positions in the store's insertion-ordered sets, which is only meaningful while those sets are
 * pure extensions of themselves — so [erasures] rides along. A deliberate forget ([ReplicatedEventStore.removePreimage] /
 * [ReplicatedEventStore.removeSealed]) renumbers everything after the record it dropped, and a cursor taken before it
 * would name different records afterwards; comparing the counts makes that detectable without the store having to know
 * who is reading it.
 *
 * A cursor is only ever obtained from a write that landed ([DurableWrite.cursor]) or from a restore of the very copy it
 * describes ([ReplicatedEventNode.cursorAfterRestore]) — the two moments a node actually knows where a copy stands.
 */
class DurableCursor internal constructor(
    internal val preimages: Int,
    internal val attestations: Int,
    internal val sealed: Int,
    internal val erasures: Int,
) {
    /** Nothing is held yet, so the next write must carry everything. */
    internal val isEmpty: Boolean get() = preimages == 0 && attestations == 0 && sealed == 0

    companion object {
        /** Where a durable copy that holds nothing stands — every node's starting point. */
        val Empty: DurableCursor = DurableCursor(0, 0, 0, 0)
    }
}

/**
 * One write a host must put into its durable copy, and where that copy stands once it has.
 *
 * The store is grow-only apart from deliberate forgetting, yet a full re-encode was paid on every flush — on a real
 * 2.4MB store that was 3.3s of a node's boot and another 3.2s of a relay's, the single largest item left on the
 * catch-up path. Nothing about the *format* forced it: batch-v1 is self-delimiting, so more `BATCH…END` blocks after
 * the last one restore exactly as one file written at once. What was missing was a way to say "only this much is new".
 *
 * The saving is the encoding, not the write: a copy with no native append (localStorage, a SQLite column) still wins by
 * reading its own contents and concatenating, because re-reading 2.4MB of text costs nothing next to re-deriving it.
 */
sealed interface DurableWrite {

    /** Exact text to put into the durable copy — never re-wrapped or re-joined by the host. */
    val text: String

    /** Where the copy stands once it holds [text]; hand it to the next [ReplicatedEventNode.encodeSince]. */
    val cursor: DurableCursor

    /**
     * Whether the copy already stands where this write would leave it, so a host may skip it entirely.
     *
     * Only an append can be vacuous. A whole write must always land: it is also what shortens a copy that holds more
     * than the node does.
     */
    val vacuous: Boolean get() = this is Appended && text.isEmpty()

    /** Replace whatever the copy holds with [text]. */
    data class Whole(override val text: String, override val cursor: DurableCursor) : DurableWrite

    /**
     * Put [text] after whatever the copy already holds, verbatim.
     *
     * [text] opens with a newline, so a host never has to know how the previous write ended — which is also what lets a
     * copy written before any of this existed be extended without being rewritten first. A blank line between batches
     * is not a data line and no batch is ever staging across the seam, so the replay ignores it.
     */
    data class Appended(override val text: String, override val cursor: DurableCursor) : DurableWrite
}
